<template lang="pug">
AlgoliaWrapper(:search-context='$algolia.searchContexts.brands')
  div(
    class='search px-3 pt-3 pb-5 text-center bg-gray-lightest bg-cover bg-no-repeat bg-center md:pt-24 md:pb-40'
    :style='bannerStyle'
  )
    div(class='lg:container')
      h1(id='brandHeader' class='text-black md:text-white') Shop by Brand
      AlgoliaQueryInput(
        aria-label='Search Brand'
        :auto-search='false'
        class='bg-[url("@/assets/search.svg")] bg-[length:20px_20px] bg-[right_10px_center] border border-gray-light rounded color-dark bg-white bg-no-repeat w-full text-sm p-3 md:max-w-screen-sm md:h-16 md:p-4 md:m-auto md:text-base'
        data-testid='brandSearch'
        placeholder='Search All Brands'
        :update-route='false'
        @query='triggerSearch'
        @focus='scrollMeTo()'
      )

  div(v-if='$device.isDesktop' class='px-3 bg-gray-lightest')
    div(class='lg:container')
      BrandsLetterList(@search='triggerSearch')

  div(class='p-3 mt-1 mb-8 md:my-8')
    div(class='lg:container')
      AlgoliaResults(class='-m-2 flex flex-wrap' data-testid='brandSearchResults')
        template(#result='{ result, index }')
          div(:key='result.objectID' class='p-3 w-1/3 lg:w-1/6')
            ImageLink(:key='result.slug' :link='brandLink(result)' :text='result.name')
              template(#image)
                img(
                  :src='getImageUrl(result.image.key, result.image.filename, 182, 76, 85, "s")'
                  :alt='result.name'
                  class='max-h-full'
                  :loading='index > 12 ? "lazy" : "eager"'
                )

      div(v-if='noResults')
        p(class='text-center text-lg') Sorry, No brands found! Please refine your search.

  div(class='px-3 py-12 bg-gray-lightest')
    div(class='lg:container')
      BrandsTextList(:data='textData')

  div(class='mx-3 lg:container')
    ConstructorioABTest
      template(#original)
        //- RECOMMENDED PRODUCTS
        CarouselProduct(v-if='$sitewideConfig.config.recommendedProductsEnabled' product-type='recommended')
        //- RECENTLY VIEWED
        CarouselProduct(product-type='recent')

      template(#constructor)
        ConstructorioRecommendations(pod-id='brands-page-1')
        ConstructorioRecommendations(pod-id='brands-page-2')
</template>

<script setup lang="ts">
const { $sitewideConfig, $algolia, $storyblok, $device, $scrollTo } = useNuxtApp()
const { translateTemplate } = useTemplateTranslator()
const { getImageUrl } = useUrls()
const { headerOffset } = useStickyOffset()
const route = useRoute()

// 404 if brands are not enabled
if (!$sitewideConfig.config.brandsEnabled) throw createError({ statusCode: 404, message: 'Invalid URL', fatal: true })

const title = translateTemplate('Brands [TITLE_SUFFIX]')
const description = translateTemplate(
  'Discover top aftermarket parts and accessories brands at [SITE]. Upgrade your vehicle with high-quality products that suit your lifestyle.'
)

function webPageSchema() {
  return {
    '@context': 'http://schema.org',
    '@type': 'WebPage',
    url: `https://${$sitewideConfig.domain}${route.path}`,
    headline: title,
    description,
    publisher: {
      '@type': 'Organization',
      name: $sitewideConfig.config.siteName,
    },
  }
}

useServerHead({
  script: [{ type: 'application/ld+json', innerHTML: webPageSchema(), tagPosition: 'bodyClose' }],
})

useMetaTags({
  title,
  description,
  socialImageUrl: $storyblok.getNormalizedAsset($sitewideConfig.config.brandsBannerFile.filename, 1920)?.src || '',
})

const { data: textData } = await useAsyncData(async () => {
  try {
    // Because none of the refinements or query get stored in the url,
    // we need to make sure that we always reset them when we come to this page.
    $algolia.searchContexts.brands.clearRefinements().setQuery()

    // trigger the full search so we can get the list of brands for the textlist
    // this is using a private method we use internally that gets the results but doesn't set the state.
    const { results } = await $algolia.searchContexts.brands.setResultsPerPage(999)._search()

    // Reset our results back down to 18 and search again
    await $algolia.searchContexts.brands.setResultsPerPage(18).search()
    return results
  } catch (error) {
    throw createError({ statusCode: 404, message: 'Invalid URL', fatal: true })
  }
})

const noResults = computed(() => {
  return $algolia.searchContexts.brands.state.value.results.length === 0
})

const bannerStyle = computed(() => {
  const style = {}

  if (!$device.value.isMobile && $sitewideConfig.config?.brandsBannerFile?.filename) {
    style.backgroundImage = `url(${$storyblok.formatSrc($sitewideConfig.config.brandsBannerFile.filename)})`
  }

  return style
})

function triggerSearch() {
  const displayCount =
    $algolia.searchContexts.brands.state.query || $algolia.searchContexts.brands.isRefined('group') ? 999 : 18

  $algolia.searchContexts.brands.setResultsPerPage(displayCount).search()
}

function scrollMeTo() {
  $scrollTo('#brandHeader', 500, { offset: -headerOffset.value })
}

function brandLink(result) {
  return `/b/${result.slug}`
}
</script>
